/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Button, IconButton, Modal, Notification } from 'components/core';
import { MdClose } from 'react-icons/md';
import getConfig from 'next/config';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useCreateMarketingProject,
  useDeleteProject,
} from 'reactQuery/mutations';

import {
  useDesignHuddleToken,
  useDoctorProfile,
  useProductDetails,
} from 'reactQuery/queries';
import { useFetchMarketingProjectDetailsByProjectId } from 'reactQuery/queries/marketingProjects';
import {
  DesignHuddleServerProject,
  DesignHuddleServerProjectPage,
  MarketingProjectStatus,
} from 'types';
import {
  CustomizedProductDetails,
  DesignHuddleEditorProps,
  getButtonLabel,
  highlightRequiredElements,
  isAllProjectPagesModified,
  isLowResolutionImageUploaded,
  LogoRequirements,
  ProjectName,
  Styled,
} from './';
import { notifyError } from 'config/setupBugsnag';
import { useI18n } from 'contexts/i18n';

const DesignHuddleEditor = ({
  projectId,
  onCloseEditor,
  sku,
  mode,
}: DesignHuddleEditorProps) => {
  const { publicRuntimeConfig } = getConfig();
  const i18n = useI18n();
  const [isProjectEdited, setIsProjectedEdited] = useState(false);
  const [isProjectNamed, setIsProjectNamed] = useState(false);
  const [isLowResolutionImagesPresent, setIsLowResolutionImagesPresent] =
    useState(false);
  const [isVideo, setIsVideo] = useState(false);
  const [editorInitialized, setEditorInitialized] = useState(false);
  const [hasMultiplePages, setHasMultiplePages] = useState(false);
  const [initialPagesData, setInitialPagesData] = useState<
    DesignHuddleServerProjectPage[]
  >([]);
  const [modifiedPagesData, setModifiedPagesData] = useState<
    DesignHuddleServerProjectPage[]
  >([]);

  const iframeEditorRef = useRef(null);
  const [isIframeContainerLoaded, setIframeContainerLoaded] = useState(false);
  const iframeElementRef = useCallback((value: any) => {
    if (value) {
      setIframeContainerLoaded(true);
    }
  }, []);

  const productDetails = useProductDetails(sku);
  const doctorProfile = useDoctorProfile();
  const reviewRequest = useFetchMarketingProjectDetailsByProjectId(projectId);
  const { data: designHuddleAccessToken } = useDesignHuddleToken(
    doctorProfile?.data?.userName
  );

  const createReviewMutation = useCreateMarketingProject();
  const deleteProjectMutation = useDeleteProject(
    doctorProfile?.data?.userName,
    isProjectNamed
  );

  useEffect(() => {
    if (
      designHuddleAccessToken &&
      projectId &&
      !iframeEditorRef.current &&
      isIframeContainerLoaded
    ) {
      //@ts-ignore
      DSHDLib.configure({
        domain: publicRuntimeConfig.DESIGN_HUDDLE_DOMAIN,
        access_token: designHuddleAccessToken,
      });
      //@ts-ignore
      DSHDLib.Editors.insert(
        document.getElementById('editoriframe'),
        { project_id: projectId },
        function (err: any, editor: any) {
          if (err) {
            notifyError('Failed to load DesignHuddle Editor', err);
          }
          iframeEditorRef.current = editor;
          //@ts-ignore
          if (editor) {
            editor.handleProjectDataChange(function () {
              //@ts-ignore
              editor.getProjectData(
                {},
                (err: any, project: DesignHuddleServerProject) => {
                  if (err) {
                    notifyError(
                      'handleProjectDataChange:: Failed to fetch project data',
                      err
                    );
                    return;
                  }

                  const projectData =
                    (project as any).pages ?? (project as any).scenes;

                  setModifiedPagesData(projectData);

                  const isVideo = (project as any).scenes !== undefined;
                  setIsVideo(isVideo);

                  const isLowResolutionImagesPresent =
                    isLowResolutionImageUploaded(project);
                  setIsLowResolutionImagesPresent(isLowResolutionImagesPresent);
                }
              );
            });

            editor.onInitialized(function () {
              setEditorInitialized(true);

              //@ts-ignore
              editor.getProjectData({}, function (err, project) {
                if (err) {
                  notifyError(
                    'onInitialized:: Failed to fetch project data',
                    err
                  );
                  return;
                }

                const isVideo = project.scenes !== undefined;
                setIsVideo(isVideo);

                // "Pages" handling posters, "Scenes" handling videos
                const projectData = project.pages ?? project.scenes;
                setInitialPagesData(projectData);
                setHasMultiplePages(isVideo ? false : project.pages?.length > 1);

                const isLowResolutionImagesPresent =
                  isLowResolutionImageUploaded(project);
                setIsLowResolutionImagesPresent(isLowResolutionImagesPresent);         
              });
            });

            editor.handlePageChange(function (
              e: any,
              data: { page_id: string }
            ) {
              editor.getProjectData(
                {},
                function (err: any, project: DesignHuddleServerProject) {
                  if (err) {
                    notifyError(
                      'handlePageChange:: Error retrieving project data:',
                      err
                    );
                    return;
                  }

                  const currentPage = (project as any).pages.filter(
                    (page: any) => page.page_id === data.page_id
                  )[0];

                  highlightRequiredElements(editor, currentPage);
                }
              );
            });

            editor.handleSelectionChange(function (e: any, elements: any) {
              const element_ids = Object.keys(elements);
              editor.highlightElements(element_ids, {
                fill_color: '#ffffff',
                duration: 50,
              });
            });
          }
        }
      );
    } else {
      notifyError('Failed to load iFrame containing DesignHuddle Editor', {
        designHuddleAccessToken,
        projectId,
        isIframeContainerLoaded,
      });
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designHuddleAccessToken, projectId, isIframeContainerLoaded]);

  useEffect(() => {
    const isModified = isAllProjectPagesModified(
      initialPagesData,
      modifiedPagesData
    );

    setIsProjectedEdited(isModified);
  }, [modifiedPagesData]);

  if (
    doctorProfile.isError ||
    productDetails.isError ||
    reviewRequest.isError
  ) {
    console.error(
      'Error loading DesignHuddle Editor related data',
      doctorProfile.error,
      productDetails.error,
      reviewRequest.error
    );
    return <>{i18n['Error Occurred']}</>;
  }

  const unLoadProject = () => {
    if (iframeEditorRef.current) {
      //@ts-ignore
      iframeEditorRef.current.remove();
    }
  };

  const onSaveHandler = () => {
    if (mode === 'create' && !isProjectEdited) {
      deleteProject();
      return;
    }

    unLoadProject();
    onCloseEditor();
  };

  const deleteProject = () => {
    deleteProjectMutation.mutate({
      projectId: projectId,
      showNotification: false,
    });
    closeEditor();
  };

  const saveNameHandler = async (projectName: string) => {
    await createReviewMutation.mutateAsync({
      projectId: projectId,
      projectName: projectName,
      sku: productDetails?.data?.sku ?? '',
      status: MarketingProjectStatus.Draft,
    });
    setIsProjectNamed(true);
    highlightRequiredElements(iframeEditorRef.current, initialPagesData[0]);
  };

  const closeEditor = () => {
    unLoadProject();
    onCloseEditor();
  };

  return (
    <Modal overlaysize="large">
      <Styled.ModalBody>
        <Styled.IframeContainer>
          <Styled.Iframe
            id="editoriframe"
            //@ts-ignore
            ref={iframeElementRef}
          ></Styled.Iframe>
        </Styled.IframeContainer>
        <Styled.ProductDetailsContainer>
          <Styled.CloseButtonWrapper>
            {!isProjectEdited && (
              <IconButton onClick={onSaveHandler} showBorder={true} size="xs">
                <MdClose />
              </IconButton>
            )}
          </Styled.CloseButtonWrapper>

          <div>
            {reviewRequest && reviewRequest.data && (
              <>
                <LogoRequirements />
                <CustomizedProductDetails
                  product={productDetails.data}
                  projectId={projectId}
                  onCloseEditor={closeEditor}
                  mode={mode}
                  isProjectEdited={isProjectEdited}
                  isLowResolutionImagesPresent={isLowResolutionImagesPresent}
                  isVideo={isVideo}
                  hasMultiplePages={hasMultiplePages}
                />
              </>
            )}
          </div>
        </Styled.ProductDetailsContainer>

        {mode === 'create' && !isProjectNamed && editorInitialized && (
          <ProjectName
            onSaveProjectName={saveNameHandler}
            isLoading={createReviewMutation.isLoading}
          />
        )}
      </Styled.ModalBody>

      <Styled.ModalFooter>
        <Styled.ActionButtons>
          <Button variant="tertiary" onClick={onSaveHandler} size={'md'}>
            {getButtonLabel(mode, isProjectEdited, reviewRequest.data?.status,  i18n)}
          </Button>
        </Styled.ActionButtons>
        {isLowResolutionImagesPresent && (
          <Notification
            title={i18n['The image you uploaded is low resolution']}
            type="error"
            isMultiLine={false}
            showCloseButton={false}
            marginLeft="10%"
          >
              {`${i18n['Please upload a high resolution image']} (${i18n['at least']} ${publicRuntimeConfig.MINIMUM_DPI_LIMIT} ppi)`}
          </Notification>
        )}
      </Styled.ModalFooter>
    </Modal>
  );
};

export default DesignHuddleEditor;
